import { useEffect, useState } from 'react';
import { useNavigate, useLocation, NavLink } from 'react-router-dom';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { STATUS_STYLES, STATUS_LABELS, STATUS_DOT } from '../../utils/statuses';
import { downloadInvoicePdf } from '../../utils/downloadInvoicePdf';
import { SkeletonList } from '../../components/Skeleton';
import EmptyState from '../../components/EmptyState';
import { totalTravellers } from '../../utils/groupSize';

function formatDate(str) {
  if (!str) return '—';
  const [y, m, d] = str.split('-');
  return `${d}/${m}/${y}`;
}

export default function CustomerDashboard() {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState(location.state?.submitted || false);
  const [invoices, setInvoices] = useState([]);
  const [downloadingId, setDownloadingId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!currentUser) return;
    const q = query(
      collection(db, 'requests'),
      where('customerId', '==', currentUser.uid)
    );
    const unsub = onSnapshot(q, (snap) => {
      const docs = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0));
      setRequests(docs);
      setLoading(false);
    });
    return unsub;
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;
    const q = query(collection(db, 'invoices'), where('customerId', '==', currentUser.uid));
    const unsub = onSnapshot(q, (snap) => {
      const docs = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0));
      setInvoices(docs);
    });
    return unsub;
  }, [currentUser]);

  async function handleLogout() {
    await logout();
    navigate('/login');
  }

  const initials = currentUser?.email?.[0]?.toUpperCase() || '?';
  const displayName = currentUser?.displayName || currentUser?.email || '';
  const activeRequests = requests.filter((r) => !['completed', 'cancelled'].includes(r.status)).length;

  return (
    <div className="min-h-screen bg-page flex flex-col">

      {/* ── Topbar ── */}
      <header className="h-14 bg-navy flex items-center justify-between px-4 flex-shrink-0 z-20">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSidebarOpen(o => !o)}
            className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 transition flex-shrink-0"
            aria-label="Toggle navigation"
          >
            <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div className="w-9 h-9 rounded-[8px] bg-ba flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold text-[12px] tracking-wide">MST</span>
          </div>
          <span className="hidden sm:inline text-white font-semibold text-[15px]">MST Sports Tours</span>
          <span className="bg-white/15 text-white text-[11px] px-3 py-0.5 rounded-[20px]">
            Customer
          </span>
        </div>

        <div className="flex items-center gap-2">
          <button aria-label="Notifications" className="relative w-[34px] h-[34px] rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition">
            <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
          </button>

          <div className="flex items-center gap-2 bg-white/10 rounded-[20px] px-3 py-1.5">
            <span className="w-6 h-6 rounded-full bg-sg flex items-center justify-center text-[11px] font-semibold text-white flex-shrink-0">
              {initials}
            </span>
            <span className="hidden sm:inline text-white text-[13px] max-w-[140px] truncate">{displayName}</span>
          </div>
        </div>
      </header>

      {/* ── Below topbar ── */}
      <div className="flex flex-1 relative">

        {/* Mobile backdrop */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/40 z-30 md:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* ── Sidebar ── */}
        <aside className={`
          flex flex-shrink-0 flex-col pt-4 pb-6 overflow-y-auto
          w-[220px] bg-white border-r border-[#E5E7EB]
          fixed top-14 bottom-0 left-0 z-40
          transition-transform duration-200 ease-in-out
          md:static md:top-auto md:bottom-auto md:z-auto md:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}>
          <nav className="px-2 space-y-0.5" onClick={() => setSidebarOpen(false)}>
            <NavLink
              to="/customer"
              end
              className={({ isActive }) =>
                `flex items-center gap-2 px-[10px] py-2 rounded-[8px] text-[13px] transition-colors ${
                  isActive
                    ? 'bg-blue-light text-pb font-semibold'
                    : 'text-[#6B7280] hover:bg-surface hover:text-[#111827]'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span className="flex-1">My Tours</span>
                  {activeRequests > 0 && (
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-[10px] text-white ${isActive ? 'bg-pb' : 'bg-ba'}`}>
                      {activeRequests}
                    </span>
                  )}
                </>
              )}
            </NavLink>

            {invoices.length > 0 && (
              <a
                href="#invoices"
                className="flex items-center gap-2 px-[10px] py-2 rounded-[8px] text-[13px] text-[#6B7280] hover:bg-surface hover:text-[#111827] transition-colors"
              >
                <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z" />
                </svg>
                <span className="flex-1">My Invoices</span>
                {invoices.some((inv) => inv.status !== 'paid') && (
                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-[10px] text-white bg-ba">
                    {invoices.filter((inv) => inv.status !== 'paid').length}
                  </span>
                )}
              </a>
            )}
          </nav>

          <div className="border-t border-[#E5E7EB] mx-[10px] my-[6px]" />

          <div className="px-2">
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 w-full px-[10px] py-2 rounded-[8px] text-[13px] text-dr hover:bg-red-light transition-colors"
            >
              Sign out
            </button>
          </div>
        </aside>

        {/* ── Main content ── */}
        <main className="flex-1 px-3 py-4 md:px-6 md:py-8 overflow-y-auto min-w-0 page-fade">

          {banner && (
            <div className="mb-6 bg-green-light border border-sg/25 text-sg text-[13px] rounded-[8px] px-4 py-3 flex items-center justify-between">
              <span>Your request has been submitted — we'll be in touch soon.</span>
              <button onClick={() => setBanner(false)} className="text-sg font-medium ml-4 hover:opacity-70 transition">Dismiss</button>
            </div>
          )}

          <div className="flex flex-wrap items-start justify-between gap-3 mb-8">
            <div>
              <h2 className="text-[20px] font-bold text-[#111827]">My Tours</h2>
              <p className="text-[#6B7280] text-[13px] mt-1">Track your tour requests and bookings.</p>
            </div>
            <button
              onClick={() => navigate('/customer/request')}
              className="bg-pb hover:bg-navy text-white text-[13px] font-semibold px-[18px] py-[9px] rounded-[8px] transition"
            >
              + New Request
            </button>
          </div>

          {loading ? (
            <SkeletonList count={3} />
          ) : requests.length === 0 ? (
            <EmptyState
              icon={
                <svg className="w-6 h-6 text-[#9CA3AF]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
                </svg>
              }
              title="No tour requests yet"
              description="Ready to plan your next sports tour? Submit a request and we'll be in touch."
              action={() => navigate('/customer/request')}
              actionLabel="Submit a Tour Request"
            />
          ) : (
            <div className="space-y-3">
              {requests.map((req) => (
                <div
                  key={req.id}
                  onClick={() => navigate(`/customer/requests/${req.id}`)}
                  className="bg-white rounded-[12px] border border-[#E5E7EB] px-5 py-4 flex items-center justify-between gap-4 cursor-pointer hover:border-pb/30 transition-colors"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <p className="font-semibold text-[#111827] truncate text-[14px]">
                        {req.sport} — {req.destination}
                      </p>
                      <span className={`inline-flex items-center gap-1 px-[9px] py-[3px] rounded-[12px] text-[11px] font-semibold whitespace-nowrap ${STATUS_STYLES[req.status] || 'bg-[#F3F4F6] text-[#6B7280]'}`}>
                        <span className={`w-[5px] h-[5px] rounded-full flex-shrink-0 ${STATUS_DOT[req.status] || 'bg-[#9CA3AF]'}`} />
                        {STATUS_LABELS[req.status] || req.status}
                      </span>
                    </div>
                    <p className="text-[13px] text-[#6B7280]">
                      {formatDate(req.departureDate)} → {formatDate(req.returnDate)}
                      &nbsp;·&nbsp;{req.groupType}
                      &nbsp;·&nbsp;{totalTravellers(req)} travellers
                    </p>
                  </div>
                  <span className="text-[12px] text-[#9CA3AF] flex-shrink-0">View →</span>
                </div>
              ))}
            </div>
          )}

          {/* Invoices */}
          {invoices.length > 0 && (
            <div className="mt-10" id="invoices">
              <div className="mb-5">
                <h2 className="text-[20px] font-bold text-[#111827]">My Invoices</h2>
                <p className="text-[#6B7280] text-[13px] mt-1">Invoices issued for your tour bookings.</p>
              </div>
              {/* Desktop table */}
              <div className="hidden md:block bg-white rounded-[12px] border border-[#E5E7EB] overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-[13px] min-w-[560px]">
                    <thead>
                      <tr className="bg-surface border-b border-[#E5E7EB]">
                        <th className="px-[18px] py-[10px] text-left text-[11px] font-bold text-[#6B7280] uppercase tracking-wide">Invoice</th>
                        <th className="px-[18px] py-[10px] text-left text-[11px] font-bold text-[#6B7280] uppercase tracking-wide">Tour</th>
                        <th className="px-[18px] py-[10px] text-left text-[11px] font-bold text-[#6B7280] uppercase tracking-wide">Amount</th>
                        <th className="px-[18px] py-[10px] text-left text-[11px] font-bold text-[#6B7280] uppercase tracking-wide">Due Date</th>
                        <th className="px-[18px] py-[10px] text-left text-[11px] font-bold text-[#6B7280] uppercase tracking-wide">Status</th>
                        <th className="px-[18px] py-[10px]" />
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map((inv) => (
                        <tr key={inv.id} className="border-b border-[#F3F4F6] hover:bg-surface transition-colors">
                          <td className="px-[18px] py-[10px] font-medium text-[#111827]">{inv.invoiceNumber || '—'}</td>
                          <td className="px-[18px] py-[10px] text-[#6B7280] text-[12px]">{inv.requestSummary || '—'}</td>
                          <td className="px-[18px] py-[10px] font-medium text-[#111827]">
                            £{Number(inv.amount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="px-[18px] py-[10px] text-[#6B7280] text-[12px]">{formatDate(inv.dueDate)}</td>
                          <td className="px-[18px] py-[10px]">
                            <span className={`inline-flex items-center gap-1 px-[9px] py-[3px] rounded-[12px] text-[11px] font-semibold ${
                              inv.paid ? 'bg-[#ECFDF5] text-[#065F46]' : 'bg-[#FFF7ED] text-[#92400E]'
                            }`}>
                              <span className={`w-[5px] h-[5px] rounded-full flex-shrink-0 ${inv.paid ? 'bg-[#10B981]' : 'bg-[#F59E0B]'}`} />
                              {inv.paid ? 'Paid' : 'Unpaid'}
                            </span>
                          </td>
                          <td className="px-[18px] py-[10px] text-right">
                            <button
                              onClick={async () => {
                                setDownloadingId(inv.id);
                                try { await downloadInvoicePdf(inv.id, inv.invoiceNumber); }
                                finally { setDownloadingId(null); }
                              }}
                              disabled={downloadingId === inv.id}
                              className="text-[12px] text-pb hover:text-navy font-medium disabled:opacity-50 transition"
                            >
                              {downloadingId === inv.id ? 'Preparing…' : 'Download PDF'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden space-y-2">
                {invoices.map((inv) => (
                  <div key={inv.id} className="bg-white rounded-[12px] border border-[#E5E7EB] px-4 py-4">
                    <div className="flex items-start justify-between gap-3 mb-1">
                      <div className="min-w-0">
                        <p className="font-semibold text-[14px] text-[#111827]">{inv.invoiceNumber || '—'}</p>
                        {inv.requestSummary && (
                          <p className="text-[12px] text-[#6B7280] mt-0.5 truncate">{inv.requestSummary}</p>
                        )}
                      </div>
                      <span className={`inline-flex items-center gap-1 px-[9px] py-[3px] rounded-[12px] text-[11px] font-semibold flex-shrink-0 ${
                        inv.paid ? 'bg-[#ECFDF5] text-[#065F46]' : 'bg-[#FFF7ED] text-[#92400E]'
                      }`}>
                        <span className={`w-[5px] h-[5px] rounded-full flex-shrink-0 ${inv.paid ? 'bg-[#10B981]' : 'bg-[#F59E0B]'}`} />
                        {inv.paid ? 'Paid' : 'Unpaid'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <div>
                        <p className="text-[15px] font-bold text-[#111827]">
                          £{Number(inv.amount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                        {inv.dueDate && (
                          <p className="text-[12px] text-[#6B7280]">Due {formatDate(inv.dueDate)}</p>
                        )}
                      </div>
                      <button
                        onClick={async () => {
                          setDownloadingId(inv.id);
                          try { await downloadInvoicePdf(inv.id, inv.invoiceNumber); }
                          finally { setDownloadingId(null); }
                        }}
                        disabled={downloadingId === inv.id}
                        className="text-[13px] text-pb hover:text-navy font-semibold disabled:opacity-50 transition py-2 px-3 bg-blue-light rounded-[8px]"
                      >
                        {downloadingId === inv.id ? 'Preparing…' : 'Download PDF'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

        </main>
      </div>
    </div>
  );
}
